import Button from '@atoms/Button/Button';
import Text from '@atoms/Text/Text';
import { IHotspotButton } from '@models/IHotspotButton';
import classNames from 'classnames';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const HotspotButton = ({
  activeTooltipIndex,
  colourCode,
  colourHexCode,
  colourName,
  colourUrl,
  colourPreviewUrl,
  hotspots,
  index,
  onToggleTooltip,
  productName,
  productUrl,
  tooltipPositionedUnderHotspot = false,
  tooltipShouldStack = false,
  tooltipOverflowLeft = false,
  tooltipOverflowRight = false,
}: IHotspotButton) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const stackedTooltipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipOffsets, setTooltipOffsets] = useState<number[]>([]);
  const [tooltipOffset, setTooltipOffset] = useState(18);
  const [stackedTooltipContainerHeight, setStackedTooltipContainerHeight] =
    useState(0);
  const [maxTooltipWidth, setMaxTooltipWidth] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const isOpen =
    activeTooltipIndex === index ||
    (tooltipShouldStack && activeTooltipIndex >= 0);

  const stackedTooltips = tooltipShouldStack && !!hotspots.length;
  const previewImageParams = 'format=jpg&height=10&width=10&quality=70';

  const handleToggleTooltip = useCallback(
    (index: number) => {
      onToggleTooltip(index);
      if (isOpen) buttonRef.current?.blur();
    },
    [index, isOpen],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      const isRelatedTargetInsideTooltip =
        tooltipRef.current?.contains(relatedTarget) ||
        stackedTooltipRefs.current.some((tooltip) =>
          tooltip?.contains(relatedTarget),
        );

      if (!isRelatedTargetInsideTooltip) {
        onToggleTooltip(-1);
      }
    },
    [onToggleTooltip],
  );

  useEffect(() => {
    if (isOpen && stackedTooltipRefs.current.length) {
      const tooltipOffsets = stackedTooltipRefs.current.map((tooltip) => {
        return tooltip ? tooltip.offsetHeight + 18 : 18;
      });
      setTooltipOffsets(tooltipOffsets);

      const maxWidth = Math.max(
        ...stackedTooltipRefs.current.map(
          (tooltip) => tooltip?.getBoundingClientRect().width || 0,
        ),
      );

      setMaxTooltipWidth(maxWidth);
    }
  }, [isOpen, hotspots.length]);

  useEffect(() => {
    if (stackedTooltips) {
      const totalHeight = stackedTooltipRefs.current.reduce((acc, tooltip) => {
        return acc + (tooltip?.offsetHeight || 0);
      }, 0);
      setStackedTooltipContainerHeight(totalHeight);
    }
  }, [stackedTooltips, tooltipOffsets]);

  const getTooltipStyles = (tooltipIndex: number) => {
    const offset = tooltipOffsets
      .slice(0, tooltipIndex)
      .reduce((acc, curr) => acc + curr - 22, 0);

    return {
      bottom: tooltipPositionedUnderHotspot ? `${offset}px` : undefined,
      top: !tooltipPositionedUnderHotspot ? `${offset}px` : undefined,
      right: tooltipOverflowRight ? `-25%` : undefined,
      left: tooltipOverflowLeft ? `-25%` : undefined,
      zIndex: 10 + tooltipIndex,
      width: `${maxTooltipWidth}px`,
    };
  };

  const singleTooltipStyles = useMemo(() => {
    return {
      bottom: tooltipPositionedUnderHotspot ? `-${tooltipOffset}px` : undefined,
      top: !tooltipPositionedUnderHotspot ? `-${tooltipOffset}px` : undefined,
      right: tooltipOverflowRight ? `-25%` : undefined,
      left: tooltipOverflowLeft ? `-25%` : undefined,
    };
  }, [
    tooltipPositionedUnderHotspot,
    tooltipOverflowLeft,
    tooltipOverflowRight,
    tooltipOffset,
  ]);

  useEffect(() => {
    if (isOpen && tooltipRef.current)
      setTooltipOffset(tooltipRef.current.offsetHeight + 18);
  }, [isOpen]);

  const tooltipClassNames = classNames(
    'tw-absolute tw-min-w-max tw-rounded tw-bg-grey-20 tw-px-4 tw-py-2',
    {
      'tooltip-bottom': tooltipPositionedUnderHotspot,
      'tooltip-right': tooltipOverflowRight,
      'tooltip-left': tooltipOverflowLeft,
      'tw-text-center': !tooltipShouldStack,
    },
  );

  return (
    <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center">
      <motion.div
        animate={
          isOpen || shouldReduceMotion ? undefined : { scale: [1, 1.05, 1] }
        }
        transition={
          isOpen || shouldReduceMotion
            ? undefined
            : { duration: 1.5, repeat: Infinity }
        }
      >
        <Button
          buttonClasses="tw-w-md !tw-h-md !tw-p-0 tw-z-10 tw-bg-opacity-50 !tw-ring-0 focus:!tw-ring-offset-0 after:tw-content-none"
          iconClasses="tw-opacity !tw-h-auto !tw-w-2 tw-shrink-0"
          iconName={isOpen ? 'cross' : 'plus'}
          onClick={() => {
            handleToggleTooltip(index);

            window.dataLayer.push({
              event: 'trackingEvent_hotspot_interaction',
              event_name_variable: 'hotspot_interaction',
              trackingEvent_hotspot_interaction: {
                product_name: productName,
                colour_name: colourName,
              },
            });
          }}
          ref={buttonRef}
          variant="white"
          aria-expanded={isOpen}
          aria-label={isOpen ? `More information ` : `Close information`}
        />
      </motion.div>

      <AnimatePresence mode="wait">
        {isOpen && (
          <>
            {stackedTooltips ? (
              <div
                className="tw-absolute -tw-right-2"
                style={{ top: `-${stackedTooltipContainerHeight + 12}px` }}
              >
                {hotspots.map(({ colour, product }, tooltipIndex) => {
                  const stackedTooltipClassNames = classNames(
                    tooltipClassNames,
                    { 'tooltip-stacked': tooltipIndex === hotspots.length - 1 },
                    { 'tooltip-separator': tooltipIndex !== 0 },
                  );

                  return (
                    <motion.div
                      key={tooltipIndex}
                      role="tooltip"
                      ref={(el) => {
                        stackedTooltipRefs.current[tooltipIndex] = el;
                      }}
                      style={getTooltipStyles(tooltipIndex)}
                      className={stackedTooltipClassNames}
                      initial={{
                        opacity: 0,
                        y: tooltipPositionedUnderHotspot ? -10 : 10,
                      }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{
                        opacity: 0,
                        y: tooltipPositionedUnderHotspot ? -10 : 10,
                      }}
                      transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
                      onBlur={(event) => handleBlur(event)}
                    >
                      {product?.name && product?.url && (
                        <a href={product.url} className="tw-group">
                          <Text
                            variant="body-small"
                            className="tw-break-words tw-text-grey-70 group-hover:tw-underline"
                          >
                            {product.name}
                          </Text>
                        </a>
                      )}

                      {colour?.name && colour?.url && (
                        <>
                          <a
                            href={colour.url}
                            className="tw-group tw-flex tw-gap-x-[5px]"
                          >
                            <span
                              className="tw-mb-auto tw-mt-1.5 tw-block tw-h-[9px] tw-min-w-[9px] tw-rounded"
                              style={{
                                backgroundImage: colour.previewUrl
                                  ? `url(${colour.previewUrl}?${previewImageParams})`
                                  : 'none',
                                backgroundColor: colour.previewUrl
                                  ? 'unset'
                                  : `#${colourHexCode}`,
                              }}
                            />
                            <Text
                              variant="body-small"
                              className="tw-break-words tw-text-grey-100 group-hover:tw-underline"
                            >
                              {colour?.colourCode} {colour.name}
                            </Text>
                          </a>
                        </>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <motion.div
                role="tooltip"
                ref={tooltipRef}
                style={singleTooltipStyles}
                className={classNames('tooltip-container', tooltipClassNames)}
                initial={{
                  opacity: 0,
                  y: tooltipPositionedUnderHotspot ? -10 : 10,
                }}
                animate={{ opacity: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  y: tooltipPositionedUnderHotspot ? -10 : 10,
                }}
                transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
                onBlur={(event) => handleBlur(event)}
              >
                {productName && productUrl && (
                  <a href={productUrl} className="tw-group">
                    <Text
                      variant="body-small"
                      className="tw-break-words tw-text-grey-100 group-hover:tw-underline"
                    >
                      {productName}
                    </Text>
                  </a>
                )}
                {colourName && colourUrl && (
                  <a
                    href={colourUrl}
                    className="tw-group tw-relative tw-flex tw-items-center tw-justify-center tw-gap-x-[5px]"
                  >
                    <span
                      className="tw-mb-auto tw-mt-1.5 tw-block tw-h-[9px] tw-min-w-[9px] tw-rounded"
                      style={{
                        backgroundImage: colourPreviewUrl
                          ? `url(${colourPreviewUrl}?${previewImageParams})`
                          : 'none',
                        backgroundColor: colourPreviewUrl
                          ? 'unset'
                          : `#${colourHexCode}`,
                      }}
                    />
                    <Text
                      variant="body-small"
                      className="tw-break-words tw-text-grey-100 group-hover:tw-underline"
                    >
                      {colourCode!} {colourName}
                    </Text>
                  </a>
                )}
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HotspotButton;
